"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

/**
 * PagemakerForAllIndustries — Section 3: 30-Branchen-Grid
 * Drop-in für: src/pages/PagemakerForAllIndustries/sections/ProductRecommendations.tsx
 *
 * 30 Branchen gruppiert in 7 Kategorien. Jede Branche mit eigener anchor-id
 * (für Footer-Links wie /pagemaker24-branchen#coiffeuse).
 */

type Branche = {
  name: string;
  slug: string;
  emoji: string;
  note: string;
};
type Group = {
  category: string;
  description: string;
  items: Branche[];
};
const groups: Group[] = [{
  category: "Beauty & Wellness",
  description: "Termine online buchbar, Behandlungen visuell präsentieren.",
  items: [{
    name: "Friseur / Coiffeuse",
    slug: "coiffeuse",
    emoji: "💇",
    note: "Online-Buchung, Schnitte zeigen, Stammkunden binden."
  }, {
    name: "Nagelstudio",
    slug: "nagelstudio",
    emoji: "💅",
    note: "Designs in Galerie zeigen, Termine direkt buchbar."
  }, {
    name: "Kosmetikerin",
    slug: "kosmetikerin",
    emoji: "✨",
    note: "Behandlungspakete erklären, Vertrauen aufbauen."
  }]
}, {
  category: "Fitness & Sport",
  description: "Programme, Buchungen und Mitgliedschaften online.",
  items: [{
    name: "Fitnessstudio",
    slug: "fitnessstudio",
    emoji: "🏋️",
    note: "Mitgliedschaften, Kursplan, Probetraining buchen."
  }, {
    name: "Fitnesstrainer / Personal Trainer",
    slug: "fitnesstrainer",
    emoji: "💪",
    note: "Pakete präsentieren, Termine buchen, Erfolge zeigen."
  }, {
    name: "Yogalehrerin / Yogastudio",
    slug: "yogastudio",
    emoji: "🧘",
    note: "Stundenplan, Anmeldung, Insights-Blog für Community."
  }]
}, {
  category: "Gastronomie",
  description: "Speisekarte, Reservierung, Google Maps — alles drin.",
  items: [{
    name: "Restaurant / Café",
    slug: "restaurant",
    emoji: "🍽️",
    note: "Digitale Speisekarte, Reservierung, lokale Sichtbarkeit."
  }, {
    name: "Bäckerei / Konditorei",
    slug: "baeckerei",
    emoji: "🥐",
    note: "Sortiment, Bestellungen, Standorte mit Öffnungszeiten."
  }, {
    name: "Catering",
    slug: "catering",
    emoji: "🍴",
    note: "Pakete, Anfrage-Formular, Galerie von Veranstaltungen."
  }, {
    name: "Food Truck",
    slug: "food-truck",
    emoji: "🚚",
    note: "Aktueller Standort, Speisekarte, Social-Updates."
  }]
}, {
  category: "Kreative & Beratung",
  description: "Vertrauen aufbauen, Expertise zeigen, Kunden gewinnen.",
  items: [{
    name: "Fotograf",
    slug: "fotograf",
    emoji: "📸",
    note: "Portfolio-Galerie, Pakete, Terminanfrage."
  }, {
    name: "Freelancer",
    slug: "freelancer",
    emoji: "💼",
    note: "Über mich, Referenzen, Kontaktformular für Aufträge."
  }, {
    name: "Anwalt / Kanzlei",
    slug: "anwalt",
    emoji: "⚖️",
    note: "Fachgebiete, Team, vertrauenserweckende Erstberatung."
  }, {
    name: "Steuerberater / Treuhänder",
    slug: "steuerberater",
    emoji: "📊",
    note: "Leistungen, Mandantenportal-Hinweis, Termin-Anfrage."
  }, {
    name: "Versicherungsberater",
    slug: "versicherungsberater",
    emoji: "🛡️",
    note: "Beratungsfelder, Vertrauen, Kontaktformular für Erstgespräch."
  }]
}, {
  category: "Handwerk & Bau",
  description: "Referenzen zeigen, Anfragen empfangen, lokal gefunden werden.",
  items: [{
    name: "Architekt / Innenarchitekt",
    slug: "architekt",
    emoji: "📐",
    note: "Projektgalerie, Philosophie, Anfrage für Erstbesprechung."
  }, {
    name: "Sanitär / Installateur",
    slug: "sanitaer",
    emoji: "🚰",
    note: "Notdienst-Hinweis, Leistungen, Click-to-Call Mobile."
  }, {
    name: "Elektriker",
    slug: "elektriker",
    emoji: "⚡",
    note: "Leistungen, Einzugsgebiet, Anfrage-Formular."
  }, {
    name: "Maler & Gipser",
    slug: "maler",
    emoji: "🎨",
    note: "Vorher/Nachher-Galerie, Referenzen, Offerte anfragen."
  }, {
    name: "Dachdecker",
    slug: "dachdecker",
    emoji: "🏠",
    note: "Leistungen, Notfall-Kontakt, Einsatzgebiet zeigen."
  }, {
    name: "Schreiner",
    slug: "schreiner",
    emoji: "🪚",
    note: "Möbel-Portfolio, Massanfertigungen, Anfrage-Formular."
  }, {
    name: "Gartenbau / Landschaftsgärtner",
    slug: "gartenbau",
    emoji: "🌿",
    note: "Projektgalerie, saisonale Angebote, Offerten anfragen."
  }]
}, {
  category: "Service & Dienstleistung",
  description: "Online buchbar, transparent, schnell erreichbar.",
  items: [{
    name: "Reinigungsservice",
    slug: "reinigungsservice",
    emoji: "🧹",
    note: "Pakete, Online-Anfrage, Bewertungen."
  }, {
    name: "Umzugsservice",
    slug: "umzugsservice",
    emoji: "📦",
    note: "Offerten-Anfrage, Referenzen, Versicherungs-Hinweis."
  }, {
    name: "Fahrschule",
    slug: "fahrschule",
    emoji: "🚗",
    note: "Kursangebote, Online-Anmeldung, Lernfahrer-Auto-Buchung."
  }]
}, {
  category: "Gesundheit & Bildung",
  description: "Termine, Vertrauen, Expertise online sichtbar.",
  items: [{
    name: "Physiotherapie",
    slug: "physiotherapie",
    emoji: "🧑‍⚕️",
    note: "Behandlungen, Krankenkassen-Hinweis, Online-Termine."
  }, {
    name: "Massagepraxis",
    slug: "massagepraxis",
    emoji: "💆",
    note: "Behandlungspakete, Buchungen, Wohlfühl-Atmosphäre online."
  }, {
    name: "Ernährungsberatung",
    slug: "ernaehrungsberatung",
    emoji: "🥗",
    note: "Beratungspakete, Erstgespräch, Insights-Blog."
  }, {
    name: "Hundesalon / Tierbetreuung",
    slug: "hundesalon",
    emoji: "🐾",
    note: "Leistungen, Galerie, Termin-Anfrage online."
  }, {
    name: "Musikschule / Musiklehrer",
    slug: "musikschule",
    emoji: "🎵",
    note: "Kurse, Lehrer-Profile, Anmelde-Formular."
  }]
}];
export default function ProductRecommendations() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  return <section id="branchen-grid" className="bg-background py-20 md:py-24">
      <div ref={ref} className={`max-w-7xl mx-auto px-4 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-4 leading-tight">
            30 Branchen — alle online sichtbar
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            Klicken Sie Ihre Branche, um direkt zur passenden Beschreibung zu
            springen.
          </p>
        </div>

        <div className="space-y-14 md:space-y-16">
          {groups.map(g => <div key={g.category}>
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-6 pb-3 border-b border-border">
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                    {g.category}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {g.description}
                  </p>
                </div>
                <div className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                  {g.items.length} Branchen
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {g.items.map(b => <div key={b.slug} id={b.slug} className="scroll-mt-32 bg-card border border-border rounded-xl p-5 hover:border-primary/40 hover:shadow-md transition-all duration-300 flex items-start gap-4">
                    <div className="text-3xl shrink-0 leading-none">
                      {b.emoji}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-base font-bold text-foreground mb-1.5 tracking-tight">
                        {b.name}
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                        {b.note}
                      </p>
                      <a href="https://dashboard.pagemaker24.ch/sign-up" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:gap-2 transition-all">
                        Website starten
                        <ArrowRight className="w-3 h-3" />
                      </a>
                    </div>
                  </div>)}
              </div>
            </div>)}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-14">
          Ihre Branche nicht dabei? Die KI baut für jedes Schweizer KMU eine
          passende Website — sprechen Sie mit uns.
        </p>
      </div>
    </section>;
}